import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  Chip,
  useTheme,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import {
  PersonOutline,
  BadgeOutlined,
  PhoneAndroidOutlined,
  EmailOutlined,
  LocationOnOutlined,
  AccountBalanceOutlined,
  CheckCircleOutline,
  ErrorOutline,
  WarningAmber,
  CloudUploadOutlined,
  DeleteOutline,
  VerifiedUserOutlined,
  BusinessOutlined,
  LockOutlined,
  Refresh,
  ArrowBack,
  ArrowForward,
  Check,
} from "@mui/icons-material";
import { Formik } from "formik";
import {
  executeAnyMutation,
  fetchJsonDynamic,
} from "../../../data/DynamicServiceNew/jsondynamicservice";
import {
  companyManagementUrl,
  customerservice,
  roleManagemenUrl,
  setupManagementUrl,
  userManagementUrl,
  loanManagementUrl,
  AccountsandFinanceurl,
  mpesaKycUrl,
} from "../../../config";
import { tokens } from "../../../theme";
import {
  isValidKenyanMobile,
  isValidIdNumber as isValidIdNumberBase,
} from "./customerValidation";

const base_url = customerservice.uri;
const setup_url = setupManagementUrl.uri;
const comp_url = companyManagementUrl.uri;
const user_url = userManagementUrl.uri;
const accounts_url = AccountsandFinanceurl.uri;

// ── Helper: normalize phone number to Safaricom format (2547XXXXXXXX / 2541XXXXXXXX) ──
const formatMsisdn = (phone) => {
  if (!phone) return "";
  let cleaned = phone.toString().trim().replace(/[\s+-]/g, "");
  if (cleaned.startsWith("0")) {
    cleaned = "254" + cleaned.slice(1);
  } else if (cleaned.startsWith("7") || cleaned.startsWith("1")) {
    cleaned = "254" + cleaned;
  }
  return cleaned;
};

// ── Helper: ID validation keyed on the form's ID type code ("05" = Passport) ──
const isValidIdNumber = (id, type) => isValidIdNumberBase(id, type === "05");

// ── Helper: normalize a value coming from a select field ────────────────────
const extractId = (val) => {
  if (val && typeof val === "object") {
    return val.value ?? val.Id ?? val.id ?? null;
  }
  return val;
};

// ── Helper: look up a bank's CharLimit by id ─────────────────────────────────
const getBankCharLimit = (bankList, bankIdLike) => {
  const rawId = extractId(bankIdLike);
  if (rawId === null || rawId === undefined || rawId === "") return null;
  const numericId = parseInt(rawId, 10);
  const match = (bankList || []).find(
    (b) => b.Id === rawId || b.Id === numericId
  );
  if (!match || match.CharLimit === null || match.CharLimit === undefined) {
    return null;
  }
  const parsedLimit = parseInt(match.CharLimit, 10);
  return Number.isNaN(parsedLimit) ? null : parsedLimit;
};
// ── Date of birth helpers ────────────────────────────────────────────────────
const MIN_CUSTOMER_AGE = 18;
const MAX_CUSTOMER_AGE = 100;

const toLocalISODate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

// Same month/day N years ago, clamped so Feb 29 doesn't roll into March
const yearsAgo = (years) => {
  const now = new Date();
  const d = new Date(now.getFullYear() - years, now.getMonth(), now.getDate());
  if (d.getMonth() !== now.getMonth()) {
    return new Date(now.getFullYear() - years, now.getMonth() + 1, 0);
  }
  return d;
};

const getDobBounds = () => ({
  min: toLocalISODate(yearsAgo(MAX_CUSTOMER_AGE)), // oldest allowed
  max: toLocalISODate(yearsAgo(MIN_CUSTOMER_AGE)), // youngest allowed (18)
});

// Returns "" when valid, otherwise a user-facing message
const getDobError = (value) => {
  if (!value) return "Date of birth is required.";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return "Enter a complete, valid date (DD/MM/YYYY).";

  const [y, m, d] = value.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) {
    return "This date does not exist.";
  }

  const { min, max } = getDobBounds();
  if (value > toLocalISODate(new Date())) return "Date of birth cannot be in the future.";
  if (value > max) return `Customer must be at least ${MIN_CUSTOMER_AGE} years old.`;
  if (value < min) return `Date of birth cannot be more than ${MAX_CUSTOMER_AGE} years ago.`;
  return "";
};

// Drops placeholder/garbage dates coming from the server (0001-01-01, 1900-01-01, etc.)
const sanitizeServerDob = (raw) => {
  if (!raw) return "";
  const value = raw.toString().split("T")[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return "";
  const { min } = getDobBounds();
  if (value < min || value > toLocalISODate(new Date())) return "";
  return value;
};

const ID_TYPES = [
  { value: "01", label: "National ID", short: "National ID" },
  { value: "05", label: "Passport", short: "Passport" },
  { value: "02", label: "Military ID", short: "Military ID" },
];

const STEPS = [
  "Identity & KYC",
  "Personal Profile",
  "Location & Address",
  "Assignment & Products",
  "Documents & Photos",
];

const CustomerForm = (props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const roleGroupId = parseInt(decodedToken.RoleGroupId || 0, 10);
  const tokenRoleGroupId = roleGroupId;

  const [activeStep, setActiveStep] = useState(0);

  const [loading, setLoading] = useState(true);
  const [maritalStatusData, setMaritalStatustData] = useState([]);
  const [titleData, setTitleData] = useState([]);
  const [genderData, setGenderData] = useState([]);
  const [yearsLivedAtResidenceData, setYearsLivedAtResidenceData] = useState([]);
  const [educationLevelData, setEducationLevelData] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [branchData, setBranchData] = useState([]);
  const [FOData, setFOData] = useState([]);
  const [Mappedbranches, setMappedBranches] = useState([]);
  const [MappedFOs, setMappedFOs] = useState([]);

  // ── Autofill & Customer Records ───────────────────────────────────────────
  const [autoFilledData, setAutoFilledData] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [formData, setFormData] = useState({
    idFrontPhoto: null,
    idBackPhoto: null,
    selfiePhoto: null,
  });
  const [customerImages, setCustomerImages] = useState({
    idFrontPhoto: null,
    idBackPhoto: null,
    selfiePhoto: null,
  });

  // ── Loan Settings & Access Flags ──────────────────────────────────────────
  const [isAssetFinanceEnabled, setIsAssetFinanceEnabled] = useState(false);
  const [isMandatoryCustomerImages, setIsMandatoryCustomerImages] = useState(false);
  const [isAssetFinanceCustomerChecked, setIsAssetFinanceCustomerChecked] = useState(false);
  const [assetFinanceAccessSettings, setAssetFinanceAccessSettings] = useState({});

  const [isDeviceEnabled, setIsDeviceEnabled] = useState(false);
  const [deviceAccessSettings, setDeviceAccessSettings] = useState({});
  const [isDeviceCustomerChecked, setIsDeviceCustomerChecked] = useState(false);

  // ── Bank details states ───────────────────────────────────────────────────
  const [bankData, setBankData] = useState([]);
  const [bankBranchData, setBankBranchData] = useState([]);
  const [MappedBankBranches, setMappedBankBranches] = useState([]);
  const [bankCharLimit, setBankCharLimit] = useState(null);

  // ── Referral states ───────────────────────────────────────────────────────
  const [isExternalReferral, setIsExternalReferral] = useState(false);
  const [agentData, setAgentData] = useState([]);

  // ── Full customer record fetched fresh from the server on edit ────────────
  const [fullCustomerData, setFullCustomerData] = useState(null);

  // ── Safaricom KYC Phone Validation State ──────────────────────────────────
  const [idType, setIdType] = useState("01"); // '01'=National ID, '05'=Passport, '02'=Military ID
  const [isSafaricomValidationEnabled, setIsSafaricomValidationEnabled] = useState(false);
  const [phoneValidation, setPhoneValidation] = useState({
    status: props.isEditing ? "valid" : "idle", // 'idle' | 'loading' | 'valid' | 'invalid'
    message: "",
    testedNumber: "",
    testedId: "",
  });

  // Fetch full customer record when editing
  useEffect(() => {
    if (props.isEditing && props.data?.Id) {
      const fetchCustomer = async () => {
        try {
          const params = { id: props.data?.Id };
          const response = await fetchJsonDynamic(
            "customersById",
            "customer",
            params,
            base_url
          );

          if (response?.results) {
            const customerData = JSON.parse(response.results)[0];
            setFullCustomerData(customerData || null);

            setCustomerImages({
              idFrontPhoto: customerData.IdFrontPhoto || null,
              idBackPhoto: customerData.IdBackPhoto || null,
              selfiePhoto: customerData.SelfiePhoto || null,
            });
          }
        } catch (error) {
          console.error("Error fetching customer record:", error);
        }
      };

      fetchCustomer();
    }
  }, [props.isEditing, props.data?.Id]);

  // Fetch setups & dropdown data
  useEffect(() => {
    async function fetchSetups() {
      try {
        const [
          maritalRes,
          residenceRes,
          eduRes,
          regionRes,
          titleRes,
          genderRes,
        ] = await Promise.all([
          fetchJsonDynamic("allMaritalStatus", null, null, setup_url),
          fetchJsonDynamic("allResidencePeriod", null, null, setup_url),
          fetchJsonDynamic("allEducationLevel", null, null, setup_url),
          fetchJsonDynamic("region", null, null, comp_url),
          fetchJsonDynamic("titles", null, null, setup_url),
          fetchJsonDynamic("allGender", null, null, setup_url),
        ]);

        if (genderRes?.results) setGenderData(JSON.parse(genderRes.results) || []);
        if (regionRes?.results) setRegionData(JSON.parse(regionRes.results) || []);
        if (titleRes?.results) setTitleData(JSON.parse(titleRes.results) || []);
        if (eduRes?.results) setEducationLevelData(JSON.parse(eduRes.results) || []);
        if (residenceRes?.results) setYearsLivedAtResidenceData(JSON.parse(residenceRes.results) || []);
        if (maritalRes?.results) setMaritalStatustData(JSON.parse(maritalRes.results) || []);
      } catch (error) {
        console.error("Error fetching setups:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchSetups();
  }, []);

  // Fetch banks
  useEffect(() => {
    async function fetchBanks() {
      try {
        const bankResponse = await fetchJsonDynamic("banks", null, null, accounts_url);
        if (bankResponse?.results) {
          setBankData(JSON.parse(bankResponse.results) || []);
        }
      } catch (error) {
        console.error("Error fetching banks:", error);
      }
    }
    fetchBanks();
  }, []);

  // Fetch agents
  useEffect(() => {
    async function fetchAgents() {
      try {
        const response = await fetchJsonDynamic(
          "allUsersByColumnValues",
          "usermodel",
          {},
          userManagementUrl.uri
        );
        if (response?.results) {
          setAgentData(JSON.parse(response.results) || []);
        }
      } catch (error) {
        console.error("Error fetching agents:", error);
      }
    }
    fetchAgents();
  }, []);

  // Fetch loan settings
  useEffect(() => {
    async function fetchLoanSettings() {
      try {
        const response = await fetchJsonDynamic("loanSettings", null, null, loanManagementUrl.uri);
        if (response?.results) {
          const settings = JSON.parse(response.results);

          const assetFinanceSetting = settings.find((s) => s.Setting === "Is Asset Finance");
          setIsAssetFinanceEnabled(assetFinanceSetting?.Value?.toLowerCase() === "true");

          const mandatoryCustomerImagesSetting = settings.find(
            (s) => s.Setting === "Mandatory Customer Images"
          );
          setIsMandatoryCustomerImages(mandatoryCustomerImagesSetting?.Value?.toLowerCase() === "true");

          const accessSettingNames = [
            "AssetCompanyAccess",
            "AssetRegionAccess",
            "AssetBranchAccess",
            "AssetFieldOfficerAccess",
          ];
          const accessMap = {};
          accessSettingNames.forEach((settingName) => {
            const found = settings.find((s) => s.Setting === settingName);
            accessMap[settingName] = found?.Value?.toLowerCase() === "true";
          });
          setAssetFinanceAccessSettings(accessMap);

          const deviceSetting = settings.find((s) => s.Setting === "Is Device");
          setIsDeviceEnabled(deviceSetting?.Value?.toLowerCase() === "true");

          const deviceAccessSettingNames = [
            "DeviceCompanyAccess",
            "DeviceRegionAccess",
            "DeviceBranchAccess",
            "DeviceFieldOfficerAccess",
          ];
          const deviceAccessMap = {};
          deviceAccessSettingNames.forEach((settingName) => {
            const found = settings.find((s) => s.Setting === settingName);
            deviceAccessMap[settingName] = found?.Value?.toLowerCase() === "true";
          });
          setDeviceAccessSettings(deviceAccessMap);
        }
      } catch (error) {
        console.error("Error fetching loan settings:", error);
      }
    }
    fetchLoanSettings();
  }, []);

  // Fetch company settings (e.g. Safaricom Mobile Validation)
  useEffect(() => {
    async function fetchCompanySettings() {
      try {
        const response = await fetchJsonDynamic("companySettings", null, null, comp_url);
        if (response?.results) {
          const settings =
            typeof response.results === "string"
              ? JSON.parse(response.results)
              : response.results;
          if (Array.isArray(settings)) {
            const safaricomSetting = settings.find(
              (s) =>
                s.Setting === "SafaricomMobileValidation" ||
                (s.EntryOrder === 5 && (s.Setting || "").toLowerCase().includes("safaricom")) ||
                s.EntryOrder === 5 ||
                s.EntryOrder === "5"
            );
            const isEnabled =
              (safaricomSetting?.Value || "").toString().trim().toLowerCase() === "true";
            setIsSafaricomValidationEnabled(isEnabled);
          }
        }
      } catch (error) {
        console.error("Error fetching company settings:", error);
      }
    }
    fetchCompanySettings();
  }, []);

  // Cascading fetchers
  const fetchBranches = async (regionId) => {
    const parsedRegionId = parseInt(extractId(regionId), 10);
    if (!parsedRegionId || Number.isNaN(parsedRegionId)) {
      setMappedBranches([]);
      setBranchData([]);
      return [];
    }
    try {
      const params = { regionId: parsedRegionId };
      const businessDetails = await fetchJsonDynamic(
        "allBranchesByColumnValues",
        "branchModel",
        params,
        companyManagementUrl.uri
      );
      const allBranchDetails = JSON.parse(businessDetails?.results) || [];
      const branchOptions = allBranchDetails.map((role) => ({
        value: role.Id,
        label: role.Name,
      }));
      setMappedBranches(branchOptions);
      setBranchData(branchOptions);
      return branchOptions;
    } catch (error) {
      console.error("Error fetching branches:", error);
      return [];
    }
  };

  const fetchFieldOfficers = async (branchId) => {
    const parsedBranchId = parseInt(extractId(branchId), 10);
    if (!parsedBranchId || Number.isNaN(parsedBranchId)) {
      setMappedFOs([]);
      setFOData([]);
      return [];
    }
    try {
      const params = { branchId: parsedBranchId, roleGroupId: 5 };
      const FODetails = await fetchJsonDynamic(
        "allUsersByColumnValues",
        "usermodel",
        params,
        userManagementUrl.uri
      );
      const allFODetails = JSON.parse(FODetails?.results) || [];
      const FOOptions = allFODetails.map((officer) => ({
        value: officer.Id,
        label: `${officer.FirstName || ""} ${officer.OtherNames || ""}`.trim() || officer.FirstName,
      }));
      setMappedFOs(FOOptions);
      setFOData(FOOptions);
      return FOOptions;
    } catch (error) {
      console.error("Error fetching Field Officers:", error);
      return [];
    }
  };

  const fetchBankBranches = async (bankId) => {
    const parsedBankId = parseInt(extractId(bankId), 10);
    if (!parsedBankId || Number.isNaN(parsedBankId)) {
      setMappedBankBranches([]);
      setBankBranchData([]);
      return [];
    }
    try {
      const params = { bankId: parsedBankId };
      const response = await fetchJsonDynamic(
        "bankBranchesByColumnValues",
        "bankBranch",
        params,
        accounts_url
      );
      const allBankBranchDetails = response?.results ? JSON.parse(response.results) : [];
      const bankBranchOptions = allBankBranchDetails.map((branch) => ({
        value: branch.Id,
        label: branch.BranchName,
      }));
      setMappedBankBranches(bankBranchOptions);
      setBankBranchData(bankBranchOptions);
      return bankBranchOptions;
    } catch (error) {
      console.error("Error fetching bank branches:", error);
      return [];
    }
  };

  // const formatDate = (date) => {
  //   if (!date) return "";
  //   return date.split("T")[0];
  // };
  const formatDate = (date) => sanitizeServerDob(date);

  // ── Customer smart-lookup function ────────────────────────────────────────
  const autoLookup = async (value, fieldType, setFieldValue) => {
    const term = value ? value.trim() : "";
    if (!term || props.isEditing) return;
    try {
      const variables =
        fieldType === "mobileNumber"
          ? { mobileNumber: term }
          : { idNoOrPassport: term };
      const response = await fetchJsonDynamic(
        "customersByColumnValuesLatest",
        "customer",
        variables,
        base_url
      );
      if (response?.results) {
        const parsed = JSON.parse(response.results);
        if (parsed && parsed.length > 0) {
          const found = parsed[0];
          if (found.RegionId) await fetchBranches(found.RegionId);
          if (found.BranchId) await fetchFieldOfficers(found.BranchId);
          if (found.BankId) await fetchBankBranches(found.BankId);
          setAutoFilledData(found);
          setFormKey((k) => k + 1);
        }
      }
    } catch (error) {
      console.error("Customer lookup error:", error);
    }
  };

  // ── Safaricom KYC Mobile Number Validation ────────────────────────────────
  const validatePhoneWithSafaricom = async (phoneVal, idVal, typeVal) => {
    if (props.isEditing) return true; // Skip in edit mode

    const cleanedPhone = formatMsisdn(phoneVal);
    if (!cleanedPhone || cleanedPhone.length < 9) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Phone Number",
        text: "Please enter a valid phone number before validating.",
      });
      return false;
    }
    if (!idVal || !idVal.trim()) {
      Swal.fire({
        icon: "warning",
        title: "ID Number Required",
        text: "Please enter the customer's ID number before validating the mobile number.",
      });
      setPhoneValidation({
        status: "invalid",
        message: "Please enter the customer's ID number before validating the mobile number.",
        testedNumber: phoneVal,
        testedId: idVal,
      });
      return false;
    }

    // Prevent duplicate validation if already verified for the same phone & ID
    if (
      phoneValidation.status === "valid" &&
      phoneValidation.testedNumber === phoneVal &&
      phoneValidation.testedId === idVal
    ) {
      return true;
    }

    setPhoneValidation({
      status: "loading",
      message: "Verifying with Safaricom KYC...",
      testedNumber: phoneVal,
      testedId: idVal,
    });

    try {
      const token = localStorage.getItem("token");
      const headers = {
        Authorization: `Bearer ${(token || "").replace(/"/g, "")}`,
        "Content-Type": "application/json",
      };
      const payload = {
        msisdn: cleanedPhone,
        idType: typeVal || "01",
        idNumber: idVal.trim(),
      };

      const response = await axios.post(mpesaKycUrl.validateMobileNumber, payload, { headers });
      const data = response?.data;

      if (data && data.isValid === true) {
        setPhoneValidation({
          status: "valid",
          message: data.message || "Details match successfully — Phone verified with Safaricom KYC.",
          testedNumber: phoneVal,
          testedId: idVal,
        });
        return true;
      } else {
        setPhoneValidation({
          status: "invalid",
          message: data?.message || "Details do not match",
          testedNumber: phoneVal,
          testedId: idVal,
        });
        return false;
      }
    } catch (err) {
      console.error("KYC validation error:", err);
      const errMsg =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred while contacting the Safaricom KYC service.";
      setPhoneValidation({
        status: "invalid",
        message: errMsg,
        testedNumber: phoneVal,
        testedId: idVal,
      });
      return false;
    }
  };

  // Source of truth for initial data
  const source = autoFilledData || fullCustomerData || props.data;

  // Pre-load region/branch/FO + bank branch when source record changes
  useEffect(() => {
    async function preloadDependents() {
      if (!source) return;

      setIsExternalReferral(
        (source.ReferralType || "").toString().toUpperCase() === "EXTERNAL"
      );
      setIsAssetFinanceCustomerChecked(!!source.IsAssetFinanceCustomer);
      setIsDeviceCustomerChecked(!!source.IsDeviceCustomer);

      if (source.RegionId) await fetchBranches(source.RegionId);
      if (source.BranchId) await fetchFieldOfficers(source.BranchId);
      if (source.BankId) await fetchBankBranches(source.BankId);
    }
    preloadDependents();
  }, [autoFilledData, fullCustomerData, props.data]);

  useEffect(() => {
    const currentBankId = source?.BankId;
    if (currentBankId) {
      setBankCharLimit(getBankCharLimit(bankData, currentBankId));
    } else {
      setBankCharLimit(null);
    }
  }, [bankData, source?.BankId]);

  const roleGroupAssetAccessSettingName = {
    1: "AssetCompanyAccess",
    2: "AssetCompanyAccess",
    3: "AssetRegionAccess",
    4: "AssetBranchAccess",
    5: "AssetFieldOfficerAccess",
  }[roleGroupId];

  const canShowAssetFinanceCheckbox =
    isAssetFinanceEnabled &&
    !!assetFinanceAccessSettings[roleGroupAssetAccessSettingName];

  const roleGroupDeviceAccessSettingName = {
    1: "DeviceCompanyAccess",
    2: "DeviceCompanyAccess",
    3: "DeviceRegionAccess",
    4: "DeviceBranchAccess",
    5: "DeviceFieldOfficerAccess",
  }[roleGroupId];

  const canShowDeviceCheckbox =
    isDeviceEnabled &&
    !!deviceAccessSettings[roleGroupDeviceAccessSettingName];

  // const today = new Date();
  // today.setFullYear(today.getFullYear() - 18);
  // const maxDateString = today.toISOString().split("T")[0];
const { min: minDobString, max: maxDobString } = getDobBounds();
  // Image helpers
  const resizeImage = (file, maxWidth = 800, maxHeight = 800, initialQuality = 0.9, maxSizeMB = 5) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = async () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;
          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height *= maxWidth / width;
              width = maxWidth;
            } else {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          let quality = initialQuality;
          const tryCompress = () =>
            new Promise((resolveTry) => {
              canvas.toBlob((blob) => resolveTry(blob), "image/jpeg", quality);
            });
          let blob = await tryCompress();
          while (blob.size / (1024 * 1024) > maxSizeMB && quality > 0.1) {
            quality -= 0.1;
            blob = await tryCompress();
          }
          if (blob.size / (1024 * 1024) <= maxSizeMB) {
            resolve(blob);
          } else {
            reject(new Error("Unable to reduce image below 5MB"));
          }
        };
        img.onerror = (e) => reject(e);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const convertFileToBase64 = (file) => {
    if (!file) return null;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result.split(",")[1]);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (fieldName, file, setFieldValue) => {
    if (file) {
      try {
        const resizedFile = await resizeImage(file);
        const base64String = await convertFileToBase64(resizedFile);
        setFormData((prev) => ({ ...prev, [fieldName]: base64String }));
        setCustomerImages((prev) => ({ ...prev, [fieldName]: base64String }));
        if (setFieldValue) {
          setFieldValue(fieldName, base64String);
        }
      } catch (error) {
        console.error(`Error processing file for ${fieldName}:`, error);
      }
    }
  };

  const handleRemovePhoto = (fieldName, setFieldValue) => {
    setFormData((prev) => ({ ...prev, [fieldName]: null }));
    setCustomerImages((prev) => ({ ...prev, [fieldName]: null }));
    if (setFieldValue) {
      setFieldValue(fieldName, null);
    }
  };

  const currentIdTypeLabel = ID_TYPES.find((t) => t.value === idType)?.label || "National ID";

  // ── Step Validation Guard: Ensures current step is clean before proceeding ──
  const validateStep = async (step, values) => {
    if (step === 0) {
      // Step 0: Identity & KYC
      if (!values.idNoOrPassport || !values.idNoOrPassport.trim()) {
        Swal.fire({
          icon: "warning",
          title: "ID Number Required",
          text: `Please enter the customer's ${currentIdTypeLabel} Number before continuing.`,
        });
        return false;
      }

      if (!values.mobileNumber || !values.mobileNumber.trim()) {
        Swal.fire({
          icon: "warning",
          title: "Mobile Number Required",
          text: "Please enter the customer's mobile number.",
        });
        return false;
      }

      if (!props.isEditing && !isValidIdNumber(values.idNoOrPassport, idType)) {
        Swal.fire({
          icon: "warning",
          title: "Invalid ID Number",
          text:
            idType === "05"
              ? "Passport number must be at least 7 characters with no spaces."
              : `${currentIdTypeLabel} must contain at least 7 digits (numbers only, no spaces).`,
        });
        return false;
      }

      if (!props.isEditing && !isValidKenyanMobile(values.mobileNumber)) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Mobile Number",
          text: "Mobile number must contain digits only and start with 01, 07 or 254 (e.g. 0712345678 or 254712345678).",
        });
        return false;
      }

      if (!props.isEditing && isSafaricomValidationEnabled) {
        if (phoneValidation.status === "loading") {
          Swal.fire({
            icon: "info",
            title: "Verification In Progress",
            text: "Please wait a moment while Safaricom KYC verifies the mobile number.",
          });
          return false;
        }

        if (phoneValidation.status === "invalid") {
          const result = await Swal.fire({
            icon: "warning",
            title: "Mobile Validation Mismatch",
            html: `
              <div style="text-align: left; font-size: 14px; line-height: 1.6;">
                <p>The mobile number <b>${values.mobileNumber}</b> could not be verified against <b>${currentIdTypeLabel} #${values.idNoOrPassport}</b>.</p>
                <div style="margin-top: 10px; padding: 10px; background-color: rgba(210, 40, 54, 0.1); border-left: 4px solid #d32f2f; border-radius: 4px;">
                  ${phoneValidation.message || "Details do not match official Safaricom records."}
                </div>
                <p style="margin-top: 12px;">Validation is optional. Do you want to proceed with registration anyway, or correct the number?</p>
              </div>
            `,
            showCancelButton: true,
            confirmButtonText: "Proceed Anyway",
            cancelButtonText: "I'll Correct It",
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d32f2f",
          });
          if (!result.isConfirmed) {
            return false;
          }
        }
      }

      if (isAssetFinanceCustomerChecked && (!values.kraPin || !values.kraPin.trim())) {
        Swal.fire({
          icon: "warning",
          title: "KRA PIN Required",
          text: "KRA PIN is required for Asset Finance customers.",
        });
        return false;
      }
      return true;
    }

    if (step === 1) {
      // Step 1: Personal Profile
      if (!values.firstName || !values.firstName.trim()) {
        Swal.fire({
          icon: "warning",
          title: "First Name Required",
          text: "Please provide the customer's First Name.",
        });
        return false;
      }
      if (!values.otherNames || !values.otherNames.trim()) {
        Swal.fire({
          icon: "warning",
          title: "Other Names Required",
          text: "Please provide the customer's Other Names.",
        });
        return false;
      }
      // if (!values.dateOfBirth) {
      //   Swal.fire({
      //     icon: "warning",
      //     title: "Date of Birth Required",
      //     text: "Please provide the customer's Date of Birth.",
      //   });
      //   return false;
      // }
      const dobError = getDobError(values.dateOfBirth);
      if (dobError) {
        Swal.fire({
          icon: "warning",
          title: "Invalid Date of Birth",
          text: dobError,
        });
        return false;
      }
      const dob = new Date(values.dateOfBirth);
      const minAge = new Date();
      minAge.setFullYear(minAge.getFullYear() - 18);
      if (dob > minAge) {
        Swal.fire({
          icon: "warning",
          title: "Age Requirement",
          text: "Customer must be at least 18 years of age.",
        });
        return false;
      }
      return true;
    }

    if (step === 2) {
      // Step 2: Location & Address (Optional fields, format check if filled)
      return true;
    }

    if (step === 3) {
      // Step 3: Assignment & Products
      if (tokenRoleGroupId !== 5) {
        if (!values.regionId) {
          Swal.fire({
            icon: "warning",
            title: "Region Required",
            text: "Please select a Region.",
          });
          return false;
        }
        if (!values.branchId) {
          Swal.fire({
            icon: "warning",
            title: "Branch Required",
            text: "Please select a Branch.",
          });
          return false;
        }
        if (!values.fieldOfficerId) {
          Swal.fire({
            icon: "warning",
            title: "Field Officer Required",
            text: "Please assign a Field Officer.",
          });
          return false;
        }
      }

      if (
        isAssetFinanceCustomerChecked &&
        bankCharLimit &&
        values.BankAccountNumber &&
        values.BankAccountNumber.toString().length !== bankCharLimit
      ) {
        Swal.fire({
          icon: "error",
          title: "Invalid Bank Account Number",
          text: `Account number must be exactly ${bankCharLimit} character${
            bankCharLimit === 1 ? "" : "s"
          } for the selected bank.`,
        });
        return false;
      }
      return true;
    }

    if (step === 4) {
      // Step 4: Documents & Photos
      if (isMandatoryCustomerImages) {
        const front = formData.idFrontPhoto || customerImages.idFrontPhoto || values.idFrontPhoto;
        const back = formData.idBackPhoto || customerImages.idBackPhoto || values.idBackPhoto;
        const selfie = formData.selfiePhoto || customerImages.selfiePhoto || values.selfiePhoto;

        if (!front || !back || !selfie) {
          Swal.fire({
            icon: "warning",
            title: "Mandatory Images Missing",
            text: "ID Front, ID Back, and Customer Selfie photos are all mandatory before submission.",
          });
          return false;
        }
      }
      return true;
    }

    return true;
  };

  const handleNext = async (values) => {
    const isClean = await validateStep(activeStep, values);
    if (isClean) {
      setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const initialValues = {
    id: source ? source.Id : null,
    firstName: source ? source.FirstName : "",
    otherNames: source ? source.OtherNames : "",
    mobileNumber: source ? source.MobileNumber : "",
    emailAddress: source ? source.EmailAddress : "",
    idNoOrPassport: source ? source.IdNoOrPassport : "",
    dateOfBirth: source ? formatDate(source.DateOfBirth) : "",
    maritalStatusId: source ? source.MaritalStatusId : "",
    postalAddress: source ? source.PostalAddress : "",
    physicalAddress: source ? source.PhysicalAddress : "",
    yearsLivedAtResidence: source ? source.YearsLivedAtResidence : "",
    educationLevelId: source ? source.EducationLevelId : "",
    postalCode: source ? source.PostalCode : "",
    regionId: source ? source.RegionId : "",
    branchId: source ? source.BranchId : "",
    fieldOfficerId: source ? source.FieldOfficerId : "",
    titleId: source ? source.TitleId : "",
    genderId: source ? source.GenderId : "",
    loanLimit: source ? source.LoanLimit : "",
    idFrontPhoto: source ? customerImages.idFrontPhoto : null,
    idBackPhoto: source ? customerImages.idBackPhoto : null,
    selfiePhoto: source ? customerImages.selfiePhoto : null,
    isAssetFinanceCustomer: source ? !!source.IsAssetFinanceCustomer : false,
    isDeviceCustomer: source ? !!source.IsDeviceCustomer : false,
    BankId: source ? source.BankId : "",
    BankBranchId: source ? source.BankBranchId : "",
    BankAccountNumber: source ? source.BankAccountNumber : "",
    IsExternalReferral: source
      ? (source.ReferralType || "").toString().toUpperCase() === "EXTERNAL"
      : false,
    ReferralAgentId: source ? source.ReferralAgentId : "",
    ReferralAgentName: source ? source.ReferralAgentName : "",
    ReferralAgentNumber: source ? source.ReferralAgentNumber : "",
    CommissionPercentage: source ? source.CommissionPercentage : "",
    kraPin: source ? source.KraPin : "",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    // Prevent accidental submit if user is not on the final step
    if (activeStep !== STEPS.length - 1) {
      setSubmitting(false);
      return;
    }

    try {
      const dobError = getDobError(values.dateOfBirth);
      if (dobError) {
        Swal.fire({ icon: "warning", title: "Invalid Date of Birth", text: dobError });
        setActiveStep(1);
        setSubmitting(false);
        return;
      }
      // Validate the final step before submitting
      const isFinalClean = await validateStep(4, values);
      if (!isFinalClean) {
        setSubmitting(false);
        return;
      }

      const referralType = isExternalReferral ? "EXTERNAL" : "INTERNAL";

      const mutationData = {
        ...(props.isEditing && { id: props.data.Id }),

        firstName: values.firstName,
        otherNames: values.otherNames,
        mobileNumber: values.mobileNumber,
        emailAddress: values.emailAddress || null,
        idNoOrPassport: values.idNoOrPassport,
        dateOfBirth: values.dateOfBirth,
        titleId: values.titleId ? parseInt(extractId(values.titleId), 10) : null,
        maritalStatusId: values.maritalStatusId ? parseInt(extractId(values.maritalStatusId), 10) : null,
        postalAddress: values.postalAddress || null,
        yearsLivedAtResidence: values.yearsLivedAtResidence || null,
        educationLevelId: values.educationLevelId ? parseInt(extractId(values.educationLevelId), 10) : null,
        postalCode: values.postalCode || null,
        physicalAddress: values.physicalAddress || null,
        kraPin: values.kraPin || null,
        isAssetFinanceCustomer: isAssetFinanceCustomerChecked ? 1 : 0,
        isDeviceCustomer: isDeviceCustomerChecked,

        regionId:
          tokenRoleGroupId !== 5
            ? parseInt(extractId(values.regionId), 10) || null
            : parseInt(decodedToken.RegionId, 10),

        branchId:
          tokenRoleGroupId !== 5
            ? parseInt(extractId(values.branchId), 10) || null
            : parseInt(decodedToken.BranchId, 10),

        genderId: values.genderId ? parseInt(extractId(values.genderId), 10) : null,

        fieldOfficerId:
          tokenRoleGroupId !== 5
            ? parseInt(extractId(values.fieldOfficerId), 10) || null
            : parseInt(decodedToken.Id, 10),

        loanLimit: values.loanLimit ? parseFloat(values.loanLimit) : null,

        idFrontPhoto: formData.idFrontPhoto || customerImages.idFrontPhoto || values.idFrontPhoto || null,
        idBackPhoto: formData.idBackPhoto || customerImages.idBackPhoto || values.idBackPhoto || null,
        selfiePhoto: formData.selfiePhoto || customerImages.selfiePhoto || values.selfiePhoto || null,

        bankBranchId: isAssetFinanceCustomerChecked ? parseInt(extractId(values.BankBranchId), 10) || null : null,
        bankId: isAssetFinanceCustomerChecked ? parseInt(extractId(values.BankId), 10) || null : null,
        bankAccountNumber: isAssetFinanceCustomerChecked ? values.BankAccountNumber : null,

        referralType: isAssetFinanceCustomerChecked ? referralType : null,
        referralAgentId:
          isAssetFinanceCustomerChecked && !isExternalReferral
            ? parseInt(extractId(values.ReferralAgentId), 10) || null
            : null,
        referralAgentName:
          isAssetFinanceCustomerChecked && isExternalReferral
            ? values.ReferralAgentName
            : null,
        referralAgentNumber:
          isAssetFinanceCustomerChecked && isExternalReferral
            ? values.ReferralAgentNumber
            : null,

        commissionPercentage: isAssetFinanceCustomerChecked && values.CommissionPercentage
          ? parseFloat(values.CommissionPercentage)
          : null,
      };

      const response = await executeAnyMutation(
        props.isEditing ? "updateCustomers" : "createCustomer",
        mutationData,
        "customer",
        false,
        base_url
      );

      if (response && response.results !== null) {
        if (props.onClose) props.onClose();

        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Customer ${props.isEditing ? "updated" : "created"} successfully.`,
          timer: 2500,
          showConfirmButton: true,
        }).then(() => {
          if (props.refetchData) props.refetchData();
          localStorage.setItem(
            "IsAssetFinanceCustomer",
            isAssetFinanceCustomerChecked ? 1 : 0
          );
          localStorage.setItem(
            "IsDeviceCustomer",
            isDeviceCustomerChecked ? 1 : 0
          );
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: response?.error || "Failed to process request. Please contact administrator.",
        });
      }
    } catch (error) {
      console.error("Submit Error:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "An error occurred while processing your request.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      key={formKey}
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        backgroundColor: colors.primary[300] || "#141b2d",
        color: colors.grey[100],
        minHeight: "100%",
        borderRadius: "14px",
      }}
    >
      {/* ── Header Card ──────────────────────────────────────────────────────── */}
      <Box
        sx={{
          mb: 2.5,
          p: { xs: 2, sm: 2.5 },
          borderRadius: "12px",
          background: `linear-gradient(135deg, ${colors.primary[400] || "#1f2a40"} 0%, ${colors.primary[500] || "#192231"} 100%)`,
          border: `1px solid ${colors.primary[300] || "#2a364f"}`,
          boxShadow: "0 6px 24px rgba(0, 0, 0, 0.18)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: "10px",
              backgroundColor: colors.greenAccent[500] || "#22bf72",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
              boxShadow: "0 4px 16px rgba(34, 191, 114, 0.35)",
            }}
          >
            {props.isEditing ? <BadgeOutlined fontSize="medium" /> : <PersonOutline fontSize="medium" />}
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.3px" }}>
              {props.isEditing ? "Edit Customer" : "New Customer Registration"}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.grey[300], fontSize: "12.5px" }}>
              Step {activeStep + 1} of {STEPS.length}: {STEPS[activeStep]}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          {!props.isEditing && isSafaricomValidationEnabled && (
            <Chip
              icon={<VerifiedUserOutlined style={{ color: "#22bf72", fontSize: 18 }} />}
              label="Safaricom KYC Active"
              size="small"
              sx={{
                backgroundColor: "rgba(34, 191, 114, 0.15)",
                color: "#22bf72",
                fontWeight: 600,
                border: "1px solid rgba(34, 191, 114, 0.3)",
              }}
            />
          )}
          {props.isEditing && (
            <Chip
              icon={<LockOutlined style={{ color: colors.grey[300], fontSize: 16 }} />}
              label="Edit Mode"
              size="small"
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.08)",
                color: colors.grey[200],
                fontWeight: 500,
              }}
            />
          )}
        </Box>
      </Box>

      {/* ── Modern Step Indicator ─────────────────────────────────────────────── */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 1.5, sm: 2 },
          borderRadius: "12px",
          backgroundColor: colors.primary[400] || "#1f2a40",
          border: `1px solid ${colors.primary[300] || "#2a364f"}`,
        }}
      >
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            "& .MuiStepLabel-label": {
              color: colors.grey[300],
              fontWeight: 500,
              fontSize: { xs: "10.5px", sm: "12px" },
              mt: 0.5,
            },
            "& .MuiStepLabel-label.Mui-active": {
              color: colors.greenAccent[400] || "#22bf72",
              fontWeight: 700,
            },
            "& .MuiStepLabel-label.Mui-completed": {
              color: colors.greenAccent[500] || "#22bf72",
            },
            "& .MuiStepIcon-root": {
              color: "rgba(255, 255, 255, 0.15)",
              fontSize: { xs: "22px", sm: "26px" },
            },
            "& .MuiStepIcon-root.Mui-active": {
              color: colors.greenAccent[500] || "#22bf72",
            },
            "& .MuiStepIcon-root.Mui-completed": {
              color: colors.greenAccent[500] || "#22bf72",
            },
          }}
        >
          {STEPS.map((label, index) => (
            <Step key={label}>
              <StepLabel
                onClick={() => {
                  if (index < activeStep) {
                    setActiveStep(index);
                  }
                }}
                sx={{ cursor: index < activeStep ? "pointer" : "default" }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* ── Formik Body ──────────────────────────────────────────────────────── */}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit: formikSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form
            onSubmit={(e) => {
              if (activeStep !== STEPS.length - 1) {
                e.preventDefault();
                return;
              }
              formikSubmit(e);
            }}
          >
            <Card
              sx={{
                borderRadius: "12px",
                backgroundColor: colors.primary[400] || "#1f2a40",
                border: `1px solid ${colors.primary[300] || "#2a364f"}`,
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                mb: 3,
                minHeight: { xs: "auto", md: "380px" },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                {/* ═══════════════════════════════════════════════════════════════
                    STEP 0: IDENTIFICATION & SAFARICOM KYC
                ════════════════════════════════════════════════════════════════ */}
                {activeStep === 0 && (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                      <VerifiedUserOutlined sx={{ color: colors.greenAccent[400] }} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {isSafaricomValidationEnabled
                          ? "Identification & Safaricom KYC"
                          : "Identification & Contact Details"}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: colors.grey[300], mb: 3, fontSize: "13px" }}>
                      {isSafaricomValidationEnabled
                        ? "Enter customer ID and mobile phone number. You can optionally verify ownership with Safaricom KYC."
                        : "Enter customer ID and mobile phone number to proceed with customer registration."}
                    </Typography>

                    <Grid container spacing={2.5}>
                      {/* Row 1: ID Document Type & ID Number (2 fields per row) */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          fullWidth
                          size="medium"
                          label="ID Document Type *"
                          name="idType"
                          value={idType}
                          disabled={props.isEditing}
                          onChange={(e) => {
                            const newType = e.target.value;
                            setIdType(newType);
                            if (!props.isEditing && phoneValidation.status !== "idle") {
                              setPhoneValidation({ status: "idle", message: "", testedNumber: "", testedId: "" });
                            }
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                            },
                          }}
                        >
                          {ID_TYPES.map((t) => (
                            <MenuItem key={t.value} value={t.value}>
                              {t.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label={`${currentIdTypeLabel} Number *`}
                          name="idNoOrPassport"
                          value={values.idNoOrPassport}
                          disabled={props.isEditing}
                          placeholder="e.g. 12345678"
                          error={!!values.idNoOrPassport && !isValidIdNumber(values.idNoOrPassport, idType)}
                          helperText={
                            values.idNoOrPassport && !isValidIdNumber(values.idNoOrPassport, idType)
                              ? idType === "05"
                                ? "Passport number must be at least 7 characters"
                                : `${currentIdTypeLabel} must be at least 7 digits`
                              : ""
                          }
                          onChange={(e) => {
                            const raw = e.target.value;
                            setFieldValue(
                              "idNoOrPassport",
                              idType === "05" ? raw.replace(/\s/g, "") : raw.replace(/\D/g, "")
                            );
                            if (!props.isEditing && phoneValidation.status !== "idle") {
                              setPhoneValidation({ status: "idle", message: "", testedNumber: "", testedId: "" });
                            }
                          }}
                          onBlur={(e) => {
                            handleBlur(e);
                            const val = e.target.value;
                            if (!props.isEditing && val && isValidIdNumber(val, idType)) {
                              autoLookup(val, "idNoOrPassport", setFieldValue);
                            }
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <BadgeOutlined sx={{ color: colors.grey[400] }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                            },
                          }}
                        />
                      </Grid>

                      {/* Row 2: Mobile Number on its own row, with Validate Button beside it if enabled */}
                      <Grid
                        item
                        xs={12}
                        sm={isSafaricomValidationEnabled && !props.isEditing ? 8 : 12}
                        md={isSafaricomValidationEnabled && !props.isEditing ? 9 : 12}
                      >
                        <TextField
                          fullWidth
                          size="medium"
                          label={isSafaricomValidationEnabled ? "Safaricom Mobile Number *" : "Mobile Number *"}
                          name="mobileNumber"
                          placeholder="e.g. 0712345678 or 254712345678"
                          value={values.mobileNumber}
                          disabled={props.isEditing}
                          inputProps={{ inputMode: "numeric", maxLength: 12 }}
                          error={!!values.mobileNumber && !isValidKenyanMobile(values.mobileNumber)}
                          helperText={
                            values.mobileNumber && !isValidKenyanMobile(values.mobileNumber)
                              ? "Must start with 01, 07 or 254 (digits only, 10 or 12 digits)"
                              : ""
                          }
                          onChange={(e) => {
                            setFieldValue("mobileNumber", e.target.value.replace(/\D/g, "").slice(0, 12));
                            if (!props.isEditing && phoneValidation.status !== "idle") {
                              setPhoneValidation({ status: "idle", message: "", testedNumber: "", testedId: "" });
                            }
                          }}
                          onBlur={(e) => {
                            handleBlur(e);
                            const val = e.target.value;
                            if (!props.isEditing && val && isValidKenyanMobile(val)) {
                              autoLookup(val, "mobileNumber", setFieldValue);
                            }
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneAndroidOutlined sx={{ color: colors.grey[400] }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                              ...(isSafaricomValidationEnabled && phoneValidation.status === "invalid" && {
                                borderColor: "#d22836 !important",
                                "& fieldset": { borderColor: "#d22836 !important", borderWidth: "2px" },
                                boxShadow: "0 0 10px rgba(210, 40, 54, 0.3)",
                              }),
                              ...(isSafaricomValidationEnabled && phoneValidation.status === "valid" && {
                                borderColor: "#22bf72 !important",
                                "& fieldset": { borderColor: "#22bf72 !important", borderWidth: "1.5px" },
                              }),
                            },
                          }}
                        />
                      </Grid>

                      {/* Safaricom Validate Button / Verified Chip beside Mobile Number on Row 2 */}
                      {isSafaricomValidationEnabled && !props.isEditing && (
                        <Grid item xs={12} sm={4} md={3}>
                          {phoneValidation.status === "valid" ? (
                            <Chip
                              icon={<CheckCircleOutline style={{ color: "#22bf72", fontSize: 18 }} />}
                              label="Verified"
                              sx={{
                                width: "100%",
                                height: "53px",
                                backgroundColor: "rgba(34, 191, 114, 0.15)",
                                color: "#22bf72",
                                fontWeight: 700,
                                fontSize: "13px",
                                border: "1.5px solid #22bf72",
                                borderRadius: "8px",
                              }}
                            />
                          ) : (
                            <Tooltip title="Validate mobile number with Safaricom KYC (Costs 4 KES)">
                              <span>
                                <Button
                                  type="button"
                                  fullWidth
                                  variant="contained"
                                  disabled={
                                    !values.mobileNumber ||
                                    !values.idNoOrPassport ||
                                    phoneValidation.status === "loading"
                                  }
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    validatePhoneWithSafaricom(values.mobileNumber, values.idNoOrPassport, idType);
                                  }}
                                  sx={{
                                    height: "53px",
                                    borderRadius: "8px",
                                    fontWeight: 700,
                                    fontSize: "13px",
                                    textTransform: "none",
                                    backgroundColor:
                                      phoneValidation.status === "invalid"
                                        ? "#d22836"
                                        : colors.blueAccent[500] || "#1f76ad",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                    "&:hover": {
                                      backgroundColor:
                                        phoneValidation.status === "invalid"
                                          ? "#b71c1c"
                                          : colors.blueAccent[600] || "#165a85",
                                    },
                                    "&.Mui-disabled": {
                                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                                      color: "rgba(255, 255, 255, 0.3)",
                                    },
                                  }}
                                  startIcon={
                                    phoneValidation.status === "loading" ? (
                                      <CircularProgress size={16} sx={{ color: "#ffffff" }} />
                                    ) : phoneValidation.status === "invalid" ? (
                                      <Refresh sx={{ fontSize: 18 }} />
                                    ) : (
                                      <VerifiedUserOutlined sx={{ fontSize: 18 }} />
                                    )
                                  }
                                >
                                  {phoneValidation.status === "loading"
                                    ? "Verifying..."
                                    : phoneValidation.status === "invalid"
                                    ? "Retry"
                                    : "Validate"}
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </Grid>
                      )}

                      {/* ── CALL TO ACTION BANNER WHEN VALIDATION IS INVALID ────── */}
                      {isSafaricomValidationEnabled && phoneValidation.status === "invalid" && !props.isEditing && (
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: "10px",
                              background: "linear-gradient(135deg, rgba(210, 40, 54, 0.16) 0%, rgba(210, 40, 54, 0.06) 100%)",
                              border: "1.5px solid #d22836",
                              boxShadow: "0 6px 20px rgba(210, 40, 54, 0.2)",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                              <WarningAmber sx={{ color: "#d22836", fontSize: "28px", mt: "2px", flexShrink: 0 }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#ff5252", fontSize: "14px" }}>
                                  ⚠️ KYC Verification Result: Mobile Number Mismatch
                                </Typography>
                                <Typography variant="body2" sx={{ color: colors.grey[100], mt: 0.3, fontSize: "13px", lineHeight: 1.5 }}>
                                  The mobile number <strong>{values.mobileNumber || "provided"}</strong> does not match {currentIdTypeLabel} <strong>{values.idNoOrPassport || "entered"}</strong> according to official Safaricom KYC verification records.
                                </Typography>

                                <Box
                                  sx={{
                                    mt: 1.2,
                                    p: 1.2,
                                    borderRadius: "6px",
                                    backgroundColor: "rgba(210, 40, 54, 0.15)",
                                    borderLeft: "4px solid #d22836",
                                  }}
                                >
                                  <Typography variant="caption" sx={{ color: "#ff8a80", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", display: "block" }}>
                                    NOTE:
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: "#ffffff", fontWeight: 500, fontSize: "12.5px", mt: 0.2 }}>
                                    Please check if the mobile number registered under <strong>{currentIdTypeLabel} #{values.idNoOrPassport}</strong> is correct, or you may proceed if this validation is not mandatory for this customer.
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {/* ── SUCCESS BANNER WHEN VALIDATION PASSES ───────────────── */}
                      {isSafaricomValidationEnabled && phoneValidation.status === "valid" && !props.isEditing && (
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              p: 1.2,
                              borderRadius: "8px",
                              background: "rgba(34, 191, 114, 0.12)",
                              border: "1px solid #22bf72",
                              display: "flex",
                              alignItems: "center",
                              gap: 1.2,
                            }}
                          >
                            <CheckCircleOutline sx={{ color: "#22bf72", fontSize: "20px" }} />
                            <Typography variant="body2" sx={{ color: "#22bf72", fontWeight: 600, fontSize: "13px" }}>
                              ✓ Safaricom KYC Verified: Mobile number matches customer {currentIdTypeLabel}.
                            </Typography>
                          </Box>
                        </Grid>
                      )}

                      {/* ── LOADING BANNER ───────────────────────────────────────── */}
                      {isSafaricomValidationEnabled && phoneValidation.status === "loading" && !props.isEditing && (
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              p: 1.2,
                              borderRadius: "8px",
                              background: "rgba(31, 118, 173, 0.12)",
                              border: "1px solid #1f76ad",
                              display: "flex",
                              alignItems: "center",
                              gap: 1.2,
                            }}
                          >
                            <CircularProgress size={16} sx={{ color: "#1f76ad" }} />
                            <Typography variant="body2" sx={{ color: "#64b5f6", fontWeight: 500, fontSize: "13px" }}>
                              Querying Safaricom KYC gateway for MSISDN and ID verification...
                            </Typography>
                          </Box>
                        </Grid>
                      )}

                      {/* Email Address */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Email Address"
                          name="emailAddress"
                          type="email"
                          placeholder="name@domain.com"
                          value={values.emailAddress}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailOutlined sx={{ color: colors.grey[400] }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                            },
                          }}
                        />
                      </Grid>

                      {/* KRA PIN */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label={`KRA PIN ${isAssetFinanceCustomerChecked ? "*" : ""}`}
                          name="kraPin"
                          placeholder="e.g. A012345678Z"
                          value={values.kraPin}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required={isAssetFinanceCustomerChecked}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* ═══════════════════════════════════════════════════════════════
                    STEP 1: PERSONAL PROFILE
                ════════════════════════════════════════════════════════════════ */}
                {activeStep === 1 && (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                      <PersonOutline sx={{ color: colors.greenAccent[400] }} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Personal Profile Details
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: colors.grey[300], mb: 3, fontSize: "13px" }}>
                      Enter the customer's legal names, gender, marital status, and date of birth.
                    </Typography>

                    <Grid container spacing={2.5}>
                      {/* Title */}
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          select
                          fullWidth
                          size="medium"
                          label="Title"
                          name="titleId"
                          value={values.titleId || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {titleData.map((item) => (
                            <MenuItem key={item.Id} value={item.Id}>
                              {item.Title}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      {/* First Name */}
                      <Grid item xs={12} sm={6} md={4.5}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="First Name *"
                          name="firstName"
                          value={values.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                            },
                          }}
                        />
                      </Grid>

                      {/* Other Names */}
                      <Grid item xs={12} sm={12} md={4.5}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Other Names *"
                          name="otherNames"
                          value={values.otherNames}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                            },
                          }}
                        />
                      </Grid>

                      {/* Gender */}
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          select
                          fullWidth
                          size="medium"
                          label="Gender"
                          name="genderId"
                          value={values.genderId || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>Select Gender</em>
                          </MenuItem>
                          {genderData.map((g) => (
                            <MenuItem key={g.Id} value={g.Id}>
                              {g.Name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      {/* Marital Status */}
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          select
                          fullWidth
                          size="medium"
                          label="Marital Status"
                          name="maritalStatusId"
                          value={values.maritalStatusId || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>Select Marital Status</em>
                          </MenuItem>
                          {maritalStatusData.map((m) => (
                            <MenuItem key={m.Id} value={m.Id}>
                              {m.Name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      {/* Date of Birth */}
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Date of Birth *"
                          name="dateOfBirth"
                          type="date"
                          value={values.dateOfBirth || ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            // Ignore values with more than 4-digit years (e.g. 020026-01-01) while typing
                            if (v && !/^\d{4}-\d{2}-\d{2}$/.test(v)) {
                              setFieldValue("dateOfBirth", v); // keep so the error shows, but it won't pass validation
                              return;
                            }
                            handleChange(e);
                          }}
                          onBlur={handleBlur}
                          required
                          error={Boolean(values.dateOfBirth) && Boolean(getDobError(values.dateOfBirth))}
                          helperText={
                            values.dateOfBirth
                              ? getDobError(values.dateOfBirth) ||
                                `Age: ${Math.floor(
                                  (new Date() - new Date(values.dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000)
                                )} years`
                              : `Must be between ${MIN_CUSTOMER_AGE} and ${MAX_CUSTOMER_AGE} years old`
                          }
                          inputProps={{ min: minDobString, max: maxDobString }}
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                            },
                          }}
                        />
                      </Grid>

                      {/* Education Level */}
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          select
                          fullWidth
                          size="medium"
                          label="Education Level"
                          name="educationLevelId"
                          value={values.educationLevelId || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>Select Education Level</em>
                          </MenuItem>
                          {educationLevelData.map((edu) => (
                            <MenuItem key={edu.Id} value={edu.Id}>
                              {edu.Name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* ═══════════════════════════════════════════════════════════════
                    STEP 2: RESIDENCE & ADDRESS
                ════════════════════════════════════════════════════════════════ */}
                {activeStep === 2 && (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                      <LocationOnOutlined sx={{ color: colors.greenAccent[400] }} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Residence & Location
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: colors.grey[300], mb: 3, fontSize: "13px" }}>
                      Provide the customer's physical residence, postal contact, and period of stay.
                    </Typography>

                    <Grid container spacing={2.5}>
                      {/* Physical Address */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Physical Address"
                          name="physicalAddress"
                          placeholder="e.g. Estate, Street, House No."
                          value={values.physicalAddress}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                            },
                          }}
                        />
                      </Grid>

                      {/* Postal Address */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Postal Address"
                          name="postalAddress"
                          placeholder="e.g. P.O. Box 1234"
                          value={values.postalAddress}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                            },
                          }}
                        />
                      </Grid>

                      {/* Postal Code */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Postal Code"
                          name="postalCode"
                          placeholder="e.g. 00100"
                          value={values.postalCode}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                            },
                          }}
                        />
                      </Grid>

                      {/* Period at Residence */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          fullWidth
                          size="medium"
                          label="Period at Residence"
                          name="yearsLivedAtResidence"
                          value={values.yearsLivedAtResidence || ""}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em>Select Period</em>
                          </MenuItem>
                          {yearsLivedAtResidenceData.map((y) => (
                            <MenuItem key={y.Id || y.Name} value={y.Name}>
                              {y.Name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* ═══════════════════════════════════════════════════════════════
                    STEP 3: ASSIGNMENT & PRODUCTS
                ════════════════════════════════════════════════════════════════ */}
                {activeStep === 3 && (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                      <BusinessOutlined sx={{ color: colors.greenAccent[400] }} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Branch Assignment & Product Options
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: colors.grey[300], mb: 3, fontSize: "13px" }}>
                      Assign operational branch and field officer, and configure product eligibility (Asset Finance, Device, Banking).
                    </Typography>

                    <Grid container spacing={2.5}>
                      {/* Operational Assignment if non-FO */}
                      {tokenRoleGroupId !== 5 ? (
                        <>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              select
                              fullWidth
                              size="medium"
                              label="Region *"
                              name="regionId"
                              value={values.regionId || ""}
                              onChange={async (e) => {
                                const regId = e.target.value;
                                handleChange(e);
                                setFieldValue("branchId", "");
                                setFieldValue("fieldOfficerId", "");
                                await fetchBranches(regId);
                              }}
                              onBlur={handleBlur}
                              required
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  backgroundColor: "rgba(0, 0, 0, 0.12)",
                                },
                              }}
                            >
                              <MenuItem value="">
                                <em>Select Region</em>
                              </MenuItem>
                              {regionData.map((r) => (
                                <MenuItem key={r.Id} value={r.Id}>
                                  {r.Name}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={12} sm={4}>
                            <TextField
                              select
                              fullWidth
                              size="medium"
                              label="Branch *"
                              name="branchId"
                              value={values.branchId || ""}
                              disabled={!values.regionId && Mappedbranches.length === 0}
                              onChange={async (e) => {
                                const bId = e.target.value;
                                handleChange(e);
                                setFieldValue("fieldOfficerId", "");
                                await fetchFieldOfficers(bId);
                              }}
                              onBlur={handleBlur}
                              required
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  backgroundColor: "rgba(0, 0, 0, 0.12)",
                                },
                              }}
                            >
                              <MenuItem value="">
                                <em>Select Branch</em>
                              </MenuItem>
                              {Mappedbranches.map((b) => (
                                <MenuItem key={b.value} value={b.value}>
                                  {b.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={12} sm={4}>
                            <TextField
                              select
                              fullWidth
                              size="medium"
                              label="Field Officer *"
                              name="fieldOfficerId"
                              value={values.fieldOfficerId || ""}
                              disabled={!values.branchId && MappedFOs.length === 0}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  backgroundColor: "rgba(0, 0, 0, 0.12)",
                                },
                              }}
                            >
                              <MenuItem value="">
                                <em>Select Field Officer</em>
                              </MenuItem>
                              {MappedFOs.map((fo) => (
                                <MenuItem key={fo.value} value={fo.value}>
                                  {fo.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        </>
                      ) : (
                        <Grid item xs={12}>
                          <Chip
                            label="Assigned automatically to your current Region, Branch, and Field Officer account."
                            sx={{ backgroundColor: "rgba(34, 191, 114, 0.15)", color: "#22bf72" }}
                          />
                        </Grid>
                      )}

                      {/* Product Flags */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.08)" }} />
                      </Grid>

                      {canShowAssetFinanceCheckbox && (
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isAssetFinanceCustomerChecked}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setIsAssetFinanceCustomerChecked(checked);
                                  setFieldValue("isAssetFinanceCustomer", checked);
                                }}
                                sx={{
                                  color: colors.greenAccent[400],
                                  "&.Mui-checked": { color: colors.greenAccent[500] },
                                }}
                              />
                            }
                            label={
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Is Asset Finance Customer?
                              </Typography>
                            }
                          />
                        </Grid>
                      )}

                      {canShowDeviceCheckbox && (
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isDeviceCustomerChecked}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setIsDeviceCustomerChecked(checked);
                                  setFieldValue("isDeviceCustomer", checked);
                                }}
                                sx={{
                                  color: colors.greenAccent[400],
                                  "&.Mui-checked": { color: colors.greenAccent[500] },
                                }}
                              />
                            }
                            label={
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Is Device Customer?
                              </Typography>
                            }
                          />
                        </Grid>
                      )}

                      {/* Loan Limit */}
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Initial Loan Limit"
                          name="loanLimit"
                          type="number"
                          value={values.loanLimit || ""}
                          disabled={props.isEditing}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="e.g. 50000"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: "rgba(0, 0, 0, 0.12)",
                            },
                          }}
                        />
                      </Grid>

                      {/* Banking Details (Asset Finance) */}
                      {isAssetFinanceCustomerChecked && (
                        <>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ color: colors.greenAccent[300], fontWeight: 700, mt: 1 }}>
                              Bank Disbursement Details
                            </Typography>
                          </Grid>

                          <Grid item xs={12} sm={4}>
                            <TextField
                              select
                              fullWidth
                              size="medium"
                              label="Bank"
                              name="BankId"
                              value={values.BankId || ""}
                              onChange={async (e) => {
                                const bId = e.target.value;
                                handleChange(e);
                                setFieldValue("BankBranchId", "");
                                setBankCharLimit(getBankCharLimit(bankData, bId));
                                await fetchBankBranches(bId);
                              }}
                              onBlur={handleBlur}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  backgroundColor: "rgba(0, 0, 0, 0.12)",
                                },
                              }}
                            >
                              <MenuItem value="">
                                <em>Select Bank</em>
                              </MenuItem>
                              {bankData.map((b) => (
                                <MenuItem key={b.Id} value={b.Id}>
                                  {b.BankName}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={12} sm={4}>
                            <TextField
                              select
                              fullWidth
                              size="medium"
                              label="Bank Branch"
                              name="BankBranchId"
                              value={values.BankBranchId || ""}
                              disabled={!values.BankId && MappedBankBranches.length === 0}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  backgroundColor: "rgba(0, 0, 0, 0.12)",
                                },
                              }}
                            >
                              <MenuItem value="">
                                <em>Select Branch</em>
                              </MenuItem>
                              {MappedBankBranches.map((bb) => (
                                <MenuItem key={bb.value} value={bb.value}>
                                  {bb.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>

                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              size="medium"
                              label={
                                bankCharLimit
                                  ? `Bank Account Number (${bankCharLimit} digits)`
                                  : "Bank Account Number"
                              }
                              name="BankAccountNumber"
                              value={values.BankAccountNumber || ""}
                              inputProps={{ maxLength: bankCharLimit || undefined }}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  backgroundColor: "rgba(0, 0, 0, 0.12)",
                                },
                              }}
                            />
                          </Grid>

                          {/* Referral Details */}
                          <Grid item xs={12}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                              <Typography variant="subtitle2" sx={{ color: colors.greenAccent[300], fontWeight: 700 }}>
                                Referral Source
                              </Typography>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={isExternalReferral}
                                    onChange={(e) => {
                                      const val = e.target.checked;
                                      setIsExternalReferral(val);
                                      setFieldValue("IsExternalReferral", val);
                                    }}
                                    sx={{
                                      color: colors.greenAccent[400],
                                      "&.Mui-checked": { color: colors.greenAccent[500] },
                                    }}
                                  />
                                }
                                label={<Typography variant="body2">External Referral?</Typography>}
                              />
                            </Box>
                          </Grid>

                          {!isExternalReferral ? (
                            <Grid item xs={12} sm={6}>
                              <TextField
                                select
                                fullWidth
                                size="medium"
                                label="Referral Agent (Internal)"
                                name="ReferralAgentId"
                                value={values.ReferralAgentId || ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    backgroundColor: "rgba(0, 0, 0, 0.12)",
                                  },
                                }}
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {agentData.map((ag) => (
                                  <MenuItem key={ag.Id} value={ag.Id}>
                                    {`${ag.FirstName || ""} ${ag.OtherNames || ""}`.trim() || ag.FirstName}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                          ) : (
                            <>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  size="medium"
                                  label="External Agent Name"
                                  name="ReferralAgentName"
                                  value={values.ReferralAgentName || ""}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "8px",
                                      backgroundColor: "rgba(0, 0, 0, 0.12)",
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  size="medium"
                                  label="External Agent Phone"
                                  name="ReferralAgentNumber"
                                  value={values.ReferralAgentNumber || ""}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "8px",
                                      backgroundColor: "rgba(0, 0, 0, 0.12)",
                                    },
                                  }}
                                />
                              </Grid>
                            </>
                          )}
                        </>
                      )}
                    </Grid>
                  </Box>
                )}

                {/* ═══════════════════════════════════════════════════════════════
                    STEP 4: DOCUMENTS & PHOTOS
                ════════════════════════════════════════════════════════════════ */}
                {activeStep === 4 && (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                      <CloudUploadOutlined sx={{ color: colors.greenAccent[400] }} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Customer KYC Documents & Photos
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: colors.grey[300], mb: 3, fontSize: "13px" }}>
                      {isMandatoryCustomerImages
                        ? "⚠️ Images are required. Upload clear photos of Front ID, Back ID, and Customer Selfie."
                        : "Upload optional supporting customer verification photos."}
                    </Typography>

                    <Grid container spacing={3}>
                      {/* ID Front Photo */}
                      <Grid item xs={12} sm={4}>
                        <Box
                          sx={{
                            border: `2px dashed ${colors.primary[300] || "#3e4d62"}`,
                            borderRadius: "12px",
                            p: 2,
                            textAlign: "center",
                            backgroundColor: "rgba(0, 0, 0, 0.1)",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              borderColor: colors.greenAccent[400],
                              backgroundColor: "rgba(34, 191, 114, 0.04)",
                            },
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                            ID Front Photo {isMandatoryCustomerImages ? "*" : ""}
                          </Typography>

                          {customerImages.idFrontPhoto ? (
                            <Box sx={{ position: "relative" }}>
                              <Box
                                component="img"
                                src={
                                  customerImages.idFrontPhoto.startsWith("data:") || customerImages.idFrontPhoto.startsWith("http")
                                    ? customerImages.idFrontPhoto
                                    : `data:image/png;base64,${customerImages.idFrontPhoto}`
                                }
                                alt="ID Front"
                                sx={{
                                  width: "100%",
                                  height: 140,
                                  objectFit: "cover",
                                  borderRadius: "8px",
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleRemovePhoto("idFrontPhoto", setFieldValue)}
                                sx={{
                                  position: "absolute",
                                  top: 6,
                                  right: 6,
                                  backgroundColor: "rgba(210, 40, 54, 0.8)",
                                  color: "#fff",
                                  "&:hover": { backgroundColor: "#d22836" },
                                }}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Button
                              component="label"
                              variant="outlined"
                              fullWidth
                              startIcon={<CloudUploadOutlined />}
                              sx={{
                                height: 140,
                                borderRadius: "8px",
                                borderColor: colors.primary[300],
                                color: colors.grey[200],
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              <Typography variant="body2" sx={{ fontSize: "12px" }}>
                                Upload Front ID
                              </Typography>
                              <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={(e) =>
                                  handleFileChange("idFrontPhoto", e.target.files[0], setFieldValue)
                                }
                              />
                            </Button>
                          )}
                        </Box>
                      </Grid>

                      {/* ID Back Photo */}
                      <Grid item xs={12} sm={4}>
                        <Box
                          sx={{
                            border: `2px dashed ${colors.primary[300] || "#3e4d62"}`,
                            borderRadius: "12px",
                            p: 2,
                            textAlign: "center",
                            backgroundColor: "rgba(0, 0, 0, 0.1)",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              borderColor: colors.greenAccent[400],
                              backgroundColor: "rgba(34, 191, 114, 0.04)",
                            },
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                            ID Back Photo {isMandatoryCustomerImages ? "*" : ""}
                          </Typography>

                          {customerImages.idBackPhoto ? (
                            <Box sx={{ position: "relative" }}>
                              <Box
                                component="img"
                                src={
                                  customerImages.idBackPhoto.startsWith("data:") || customerImages.idBackPhoto.startsWith("http")
                                    ? customerImages.idBackPhoto
                                    : `data:image/png;base64,${customerImages.idBackPhoto}`
                                }
                                alt="ID Back"
                                sx={{
                                  width: "100%",
                                  height: 140,
                                  objectFit: "cover",
                                  borderRadius: "8px",
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleRemovePhoto("idBackPhoto", setFieldValue)}
                                sx={{
                                  position: "absolute",
                                  top: 6,
                                  right: 6,
                                  backgroundColor: "rgba(210, 40, 54, 0.8)",
                                  color: "#fff",
                                  "&:hover": { backgroundColor: "#d22836" },
                                }}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Button
                              component="label"
                              variant="outlined"
                              fullWidth
                              startIcon={<CloudUploadOutlined />}
                              sx={{
                                height: 140,
                                borderRadius: "8px",
                                borderColor: colors.primary[300],
                                color: colors.grey[200],
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              <Typography variant="body2" sx={{ fontSize: "12px" }}>
                                Upload Back ID
                              </Typography>
                              <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={(e) =>
                                  handleFileChange("idBackPhoto", e.target.files[0], setFieldValue)
                                }
                              />
                            </Button>
                          )}
                        </Box>
                      </Grid>

                      {/* Customer Selfie Photo */}
                      <Grid item xs={12} sm={4}>
                        <Box
                          sx={{
                            border: `2px dashed ${colors.primary[300] || "#3e4d62"}`,
                            borderRadius: "12px",
                            p: 2,
                            textAlign: "center",
                            backgroundColor: "rgba(0, 0, 0, 0.1)",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              borderColor: colors.greenAccent[400],
                              backgroundColor: "rgba(34, 191, 114, 0.04)",
                            },
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                            Customer Selfie Photo {isMandatoryCustomerImages ? "*" : ""}
                          </Typography>

                          {customerImages.selfiePhoto ? (
                            <Box sx={{ position: "relative" }}>
                              <Box
                                component="img"
                                src={
                                  customerImages.selfiePhoto.startsWith("data:") || customerImages.selfiePhoto.startsWith("http")
                                    ? customerImages.selfiePhoto
                                    : `data:image/png;base64,${customerImages.selfiePhoto}`
                                }
                                alt="Selfie"
                                sx={{
                                  width: "100%",
                                  height: 140,
                                  objectFit: "cover",
                                  borderRadius: "8px",
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleRemovePhoto("selfiePhoto", setFieldValue)}
                                sx={{
                                  position: "absolute",
                                  top: 6,
                                  right: 6,
                                  backgroundColor: "rgba(210, 40, 54, 0.8)",
                                  color: "#fff",
                                  "&:hover": { backgroundColor: "#d22836" },
                                }}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Button
                              component="label"
                              variant="outlined"
                              fullWidth
                              startIcon={<CloudUploadOutlined />}
                              sx={{
                                height: 140,
                                borderRadius: "8px",
                                borderColor: colors.primary[300],
                                color: colors.grey[200],
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              <Typography variant="body2" sx={{ fontSize: "12px" }}>
                                Upload Selfie
                              </Typography>
                              <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={(e) =>
                                  handleFileChange("selfiePhoto", e.target.files[0], setFieldValue)
                                }
                              />
                            </Button>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </CardContent>

              {/* ── Modern Step Navigation Bar ───────────────────────────────── */}
              <Box
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  borderTop: `1px solid rgba(255, 255, 255, 0.08)`,
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1.5,
                }}
              >
                {/* Left: Cancel and Back */}
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  {props.onClose && (
                    <Button
                      variant="text"
                      onClick={props.onClose}
                      sx={{
                        color: colors.grey[300],
                        "&:hover": { color: colors.grey[100] },
                      }}
                    >
                      Cancel
                    </Button>
                  )}

                  {activeStep > 0 && (
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={handleBack}
                      startIcon={<ArrowBack />}
                      sx={{
                        borderRadius: "8px",
                        px: 2.5,
                        py: 0.8,
                        color: colors.grey[200],
                        borderColor: colors.primary[300],
                        "&:hover": {
                          borderColor: colors.grey[400],
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                        },
                      }}
                    >
                      Back
                    </Button>
                  )}
                </Box>

                {/* Right: Next or Submit */}
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                  {activeStep < STEPS.length - 1 ? (
                    <Button
                      type="button"
                      variant="contained"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleNext(values);
                      }}
                      endIcon={<ArrowForward />}
                      sx={{
                        borderRadius: "8px",
                        px: 3.5,
                        py: 1,
                        fontWeight: 700,
                        backgroundColor: colors.greenAccent[500] || "#22bf72",
                        color: "#ffffff",
                        boxShadow: "0 4px 14px rgba(34, 191, 114, 0.4)",
                        "&:hover": {
                          backgroundColor: colors.greenAccent[600] || "#1b995b",
                        },
                      }}
                    >
                      Next Step
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? null : <Check />}
                      sx={{
                        borderRadius: "8px",
                        px: 4,
                        py: 1.1,
                        fontWeight: 700,
                        backgroundColor: colors.greenAccent[500] || "#22bf72",
                        color: "#ffffff",
                        boxShadow: "0 4px 16px rgba(34, 191, 114, 0.5)",
                        "&:hover": {
                          backgroundColor: colors.greenAccent[600] || "#1b995b",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "rgba(255, 255, 255, 0.12)",
                          color: "rgba(255, 255, 255, 0.3)",
                        },
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={22} sx={{ color: "#ffffff" }} />
                      ) : props.isEditing ? (
                        "Save Customer Changes"
                      ) : (
                        "Complete & Create Customer"
                      )}
                    </Button>
                  )}
                </Box>
              </Box>
            </Card>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CustomerForm;